export const defaultDropdownOptions = {
    placement: 'bottom-start',
    offset: [0, 12],
    closeOtherDropdowns: true,
};
export function toggleDropdown(evt, options) {
    if (!(evt.target instanceof Element))
        return;
    const btn = evt.target.closest('.btn-dropdown');
    if (!btn || !(btn instanceof HTMLElement))
        return;
    const el = btn?.parentElement?.querySelector('.dropdown') ?? null;
    const opts = { ...defaultDropdownOptions, ...options };
    if (!el)
        return;
    if (btn.classList.contains('expanded')) {
        closeDropdown(btn, el);
    }
    else {
        openDropdown(btn, el, opts);
    }
}
export function closeParentDropdown(evt) {
    if (!(evt.target instanceof Element))
        return;
    const dropdown = evt.target.closest('.dropdown');
    if (dropdown) {
        const dropdownBtn = dropdown.parentElement?.querySelector('.btn-dropdown');
        if (dropdownBtn instanceof HTMLElement) {
            closeDropdown(dropdownBtn, dropdown);
        }
    }
}
export function closeDropdown(btn, el) {
    // Collapse any nested (flyout) submenu so reopening the parent doesn't
    // leave a stale, fixed-positioned submenu floating on screen.
    el.querySelectorAll('.btn-dropdown.expanded').forEach((b) => b.classList.remove('expanded'));
    el.querySelectorAll('.dropdown:not(.hidden)').forEach((d) => d.classList.add('hidden'));
    el.classList.add('hidden');
    btn.classList.remove('expanded');
    btn.setAttribute('aria-expanded', 'false');
    btn.removeAttribute('data-dropdown-placement');
    btn.removeAttribute('data-dropdown-offset');
    document.removeEventListener('click', closeOnClickOutside);
    window.removeEventListener('scroll', closeAllDropdowns, { capture: true });
    window.removeEventListener('resize', repositionAllDropdowns);
}
export function positionDropdown(btn, el, placement, offset) {
    const resolvedPlacement = placement ?? btn.dataset.dropdownPlacement ?? defaultDropdownOptions.placement;
    const resolvedOffset = offset ?? (btn.dataset.dropdownOffset ? JSON.parse(btn.dataset.dropdownOffset) : defaultDropdownOptions.offset);
    const p = resolvedPlacement.split('-');
    const place = p[0];
    const align = (p[1] || 'center');
    const width = el.offsetWidth;
    const height = el.offsetHeight;
    const btnRect = btn.getBoundingClientRect();
    let left = 0;
    let top = 0;
    if (place === 'right' || place === 'left') {
        // --- Side (flyout) placement: align is vertical ---
        if (place === 'right') {
            left = btnRect.right + resolvedOffset[0];
        }
        else {
            left = btnRect.left - width - resolvedOffset[0];
        }
        if (align === 'start') {
            top = btnRect.top + resolvedOffset[1];
        }
        else if (align === 'end') {
            top = btnRect.bottom - height + resolvedOffset[1];
        }
        else {
            top = btnRect.top + btnRect.height / 2 - height / 2 + resolvedOffset[1];
        }
        // Horizontal flip if the chosen side overflows the viewport
        if (place === 'right' && left + width > window.innerWidth) {
            left = btnRect.left - width - resolvedOffset[0];
        }
        if (place === 'left' && left < 0) {
            left = btnRect.right + resolvedOffset[0];
        }
        // Final horizontal clamp
        if (left + width > window.innerWidth) {
            left = window.innerWidth - width - 10;
        }
        if (left < 0) {
            left = 10;
        }
        // Vertical clamp into the viewport
        if (top + height > window.innerHeight) {
            top = window.innerHeight - height - 10;
        }
        if (top < 0) {
            top = 10;
        }
    }
    else {
        const vPlace = place;
        const hPlace = align;
        // --- Calculate Horizontal Position (Relative to Viewport) ---
        if (hPlace === 'start') {
            left = btnRect.left + resolvedOffset[0];
        }
        else if (hPlace === 'end') {
            left = btnRect.right - width + resolvedOffset[0];
        }
        else {
            left = btnRect.left + (btnRect.width / 2) - (width / 2) + resolvedOffset[0];
        }
        // --- Calculate Vertical Position (Relative to Viewport) ---
        if (vPlace === 'top') {
            top = btnRect.top - height - resolvedOffset[1];
        }
        else {
            top = btnRect.bottom + resolvedOffset[1];
        }
        // --- Fallback Adjustments for Viewport Bounds ---
        // Right edge collision
        if (left + width > window.innerWidth) {
            left = window.innerWidth - width - 10;
        }
        // Left edge collision
        if (left < 0) {
            left = 10;
        }
        // Bottom edge collision
        if (top + height > window.innerHeight) {
            if (vPlace === 'bottom') {
                top = btnRect.top - height - resolvedOffset[1]; // Flip to top
            }
            // Secondary check in case flipping top still overflows
            if (top < 0) {
                top = 10;
            }
        }
        // Top edge collision
        if (top < 0) {
            if (vPlace === 'top') {
                top = btnRect.bottom + resolvedOffset[1]; // Flip to bottom
            }
        }
    }
    el.style.left = left + 'px';
    el.style.top = top + 'px';
}
export function openDropdown(btn, el, opts) {
    el.classList.remove('hidden');
    btn.classList.add('expanded');
    btn.setAttribute('aria-expanded', 'true');
    // Store placement options as data-attributes for repositioning on scroll/resize
    btn.dataset.dropdownPlacement = opts.placement;
    btn.dataset.dropdownOffset = JSON.stringify(opts.offset);
    positionDropdown(btn, el, opts.placement, opts.offset);
    if (opts.closeOtherDropdowns) {
        document.querySelectorAll('.btn-dropdown').forEach((otherBtn) => {
            if (!btn.isEqualNode(otherBtn)) {
                otherBtn?.classList.remove('expanded');
                if (otherBtn instanceof HTMLElement)
                    otherBtn.setAttribute('aria-expanded', 'false');
                const dropdown = otherBtn.parentElement?.querySelector('.dropdown');
                if (dropdown && dropdown.classList.contains('dropdown')) {
                    dropdown.classList.add('hidden');
                }
            }
        });
    }
    // If this is a nested submenu trigger, collapse sibling submenus that live
    // in the same parent dropdown (so e.g. Import/Export are mutually exclusive)
    // without closing the parent dropdown itself. Top-level triggers have no
    // ancestor `.dropdown`, so this is a no-op for them.
    const parentDropdown = btn.closest('.dropdown');
    if (parentDropdown) {
        parentDropdown.querySelectorAll('.btn-dropdown.expanded').forEach((otherBtn) => {
            if (otherBtn === btn || !(otherBtn instanceof HTMLElement))
                return;
            otherBtn.classList.remove('expanded');
            const dropdown = otherBtn.parentElement?.querySelector('.dropdown');
            if (dropdown && dropdown.classList.contains('dropdown')) {
                dropdown.classList.add('hidden');
            }
        });
    }
    // Delay attaching click listener so the trigger click doesn't instantly close it
    setTimeout(() => {
        document.addEventListener('click', closeOnClickOutside);
        // Use capture: true to catch scrolling on ANY element, not just the window
        window.addEventListener('scroll', closeAllDropdowns, { capture: true, passive: true });
        window.addEventListener('resize', repositionAllDropdowns, { passive: true });
    }, 0);
}
// Reposition all currently open dropdowns (e.g. on resize)
function repositionAllDropdowns() {
    document.querySelectorAll('.btn-dropdown.expanded').forEach((btn) => {
        if (!(btn instanceof HTMLElement))
            return;
        const dropdown = btn.parentElement?.querySelector('.dropdown');
        if (dropdown && !dropdown.classList.contains('hidden')) {
            positionDropdown(btn, dropdown);
        }
    });
}
// Simplified global closer for scroll and outside clicks
export function closeAllDropdowns() {
    document.querySelectorAll('.dropdown:not(.hidden)').forEach((dropdown) => {
        if (dropdown instanceof HTMLElement) {
            dropdown.classList.add('hidden');
            const dropdownBtn = dropdown.parentElement?.querySelector('.btn-dropdown');
            if (dropdownBtn instanceof HTMLElement) {
                dropdownBtn.classList.remove('expanded');
                dropdownBtn.setAttribute('aria-expanded', 'false');
                dropdownBtn.removeAttribute('data-dropdown-placement');
                dropdownBtn.removeAttribute('data-dropdown-offset');
            }
        }
    });
    document.removeEventListener('click', closeOnClickOutside);
    window.removeEventListener('scroll', closeAllDropdowns, { capture: true });
    window.removeEventListener('resize', repositionAllDropdowns);
}
export function closeOnClickOutside(e) {
    if (!(e.target instanceof HTMLElement))
        return;
    const btn = e.target.closest('.btn-dropdown');
    const isDropdownMenu = e.target.closest('.dropdown');
    if (!btn && !isDropdownMenu) {
        closeAllDropdowns();
    }
}
